// headOfLineUtils.js
import { normalizeRelation } from './normalizeRelation';

/**
 * Check if a member is a Head of Line (direct child of Top of Line)
 */
export function isHeadOfLineMember(parentMember) {
    return parentMember && parentMember.generation === 1;
}

/**
 * Get Head of Line relation for primary member
 */
export function getHeadOfLineRelation(baseRelation, generation) {
    // Generation 2: Direct children of Top of Line (Head of Line)
    if (generation == 2) {
        return baseRelation === "Son" ? "Son" : 
               baseRelation === "Daughter" ? "Daughter" : "Child";
    }
    
    // Generation 3: Grandchildren of Top of Line
    if (generation == 3) {
        return baseRelation === "Son" ? "Grandson" : 
               baseRelation === "Daughter" ? "Granddaughter" : "Grandchild";
    }
    
    // Generation 4: Great-Grandchildren of Top of Line
    if (generation == 4) {
        return baseRelation === "Son" ? "Great-Grandson" : 
               baseRelation === "Daughter" ? "Great-Granddaughter" : "Great-Grandchild";
    }
    
    // Generation 5+: Great-Great-Grandchildren etc.
    if (generation >= 5) {
        const greatCount = generation - 3;
        const prefix = greatCount === 2 ? "Great-Great-" : 
                       `${'Great-'.repeat(greatCount)}`;
        return baseRelation === "Son" ? `${prefix}Grandson` : 
               baseRelation === "Daughter" ? `${prefix}Granddaughter` : 
               `${prefix}Descendant`;
    }
    
    return "Relative";
}

/**
 * Get Head of Line relation for spouse
 */
export function getSpouseHeadOfLineRelation(baseRelation, generation) {
    // Generation 2: Spouses of Head of Line
    if (generation === 2) {
        return baseRelation === "Son" ? "Wife of Head" : "Husband of Head";
    }
    
    // Generation 3: Spouses of Grandchildren
    if (generation === 3) {
        return baseRelation === "Son" ? "Wife of Son of Head" : "Husband of Daughter of Head";
    }
    
    // Generation 4: Spouses of Great-Grandchildren
    if (generation === 4) {
        return baseRelation === "Son" ? "Wife of Grandson of Head" : "Husband of Granddaughter of Head";
    }
    
    // Generation 5+: Spouses of Great-Great-Grandchildren etc.
    if (generation >= 5) {
        const greatCount = generation - 3;
        const prefix = greatCount === 2 ? "Great-Great-" : 
                       `${'Great-'.repeat(greatCount)}`;
        return baseRelation === "Son" ? `Wife of ${prefix}Grandson of Head` : 
               baseRelation === "Daughter" ? `Husband of ${prefix}Granddaughter of Head` : 
               `Spouse of ${prefix}Descendant of Head`;
    }
    
    return "Spouse of Head";
}

/**
 * Calculate Head of Line labels and relations
 */
export function getHeadOfLineLabels(isRootGeneration, currentGeneration, rootHeadGenerationName, isHeadOfLine = false) {
    let primaryHeadOfLineRelation = "";
    let spouseHeadOfLineRelation = "";

    if (isRootGeneration) {
        // Top of Line member (generation 1)
        primaryHeadOfLineRelation = "Self Head of Line";
        spouseHeadOfLineRelation = "Spouse of Head of Line";
    } else if (isHeadOfLine) {
        // Head of Line member (generation 2)
        primaryHeadOfLineRelation = "Self Head of Line";
        spouseHeadOfLineRelation = rootHeadGenerationName === "Son" ? "Wife of Head" : "Husband of Head";
    } else {
        // Descendants of Head of Line (generation 3+)
        primaryHeadOfLineRelation = getHeadOfLineRelation(rootHeadGenerationName, currentGeneration);
        spouseHeadOfLineRelation = getSpouseHeadOfLineRelation(rootHeadGenerationName, currentGeneration);
    }

    return {
        primaryHeadOfLineRelation,
        spouseHeadOfLineRelation,
    };
}


// Add this to headOfLineUtils.js

/**
 * Legacy function for backward compatibility - calculates Head of Line relation
 */
export function calculateHeadOfLineRelation(
  isRootGen, 
  generation, 
  headName, 
  currentRelation, 
  isHeadOfLine = false,
  isSpouse = false,
  spouseRelation = ""
) {
  // If this person IS the head of line themselves
  if (isHeadOfLine) {
    // return "Self (Head of Line)";
    return "Self (Head of Line)";
  }
  
  // Root head is the Top of Line (generation 1)
  if (isRootGen) {
    return "Self (Top of Line)";
  }
  
  // For spouses, derive relation from their spouse's relation
  if (isSpouse) {
    const relation = currentRelation || spouseRelation || "";
    const lowerRelation = relation.toLowerCase();
    
    // Handle specific spouse cases
    if (lowerRelation.includes("son of head") || relation === "Son of Head") {
      return headName === "Son" ? "Wife of Head" : "Husband of Head";
    } else if (lowerRelation.includes("daughter of head") || relation === "Daughter of Head") {
      return headName === "Daughter" ? "Husband of Head" : "Wife of Head";
    } else if (lowerRelation.includes("grandson of head")) {
      return "Wife of Grandson of Head";
    } else if (lowerRelation.includes("granddaughter of head")) {
      return "Husband of Granddaughter of Head";
    } else if (lowerRelation.includes("great-grandson of head")) {
      return "Wife of Great-Grandson of Head";
    } else if (lowerRelation.includes("great-granddaughter of head")) {
      return "Husband of Great-Granddaughter of Head";
    }
    return "Spouse of Head";
  }
  
  // For direct children of Top of Line (generation 2) - these are the Heads of Line
  if (generation === 2) {
    return headName === "Son" ? "Son" : 
           headName === "Daughter" ? "Daughter" : "Child";
  }
  
  // For grandchildren of Head of Line (generation 3)
  if (generation === 3) {
    return headName === "Son" ? "Grandson" : 
           headName === "Daughter" ? "Granddaughter" : "Grandchild";
  }
  
  // For great-grandchildren of Head of Line (generation 4)
  if (generation === 4) {
    return headName === "Son" ? "Great-Grandson" : 
           headName === "Daughter" ? "Great-Granddaughter" : "Great-Grandchild";
  }
  
  // For generations beyond 4
  if (generation >= 5) {
    const greatCount = generation - 3;
    const prefix = greatCount === 2 ? "Great-Great-" : 
                   `${'Great-'.repeat(greatCount)}`;
    return headName === "Son" ? `${prefix}Grandson` : 
           headName === "Daughter" ? `${prefix}Granddaughter` : 
           `${prefix}Descendant`;
  }
  
  return currentRelation || "Relative";
}



/**
 * Simple function to calculate Head of Line relations based on Top of Line relations
 */
// export function calculateHeadOfLineFromFamilyLabels(familyLabels, isRootGeneration = false) {
//   const { primaryRelation, spouseRelation } = familyLabels;
  
//   // Primary member Head of Line relation
//   let primaryHeadOfLine = "";
//   if (isRootGeneration) {
//     primaryHeadOfLine = "Self (Head of Line)";
//   } else if (primaryRelation.includes("Son") && !primaryRelation.includes("Grand")) {
//     primaryHeadOfLine = "Self (Head of Line)";
//   } else if (primaryRelation.includes("Daughter") && !primaryRelation.includes("Grand")) {
//     primaryHeadOfLine = "Self (Head of Line)";
//   } else if (primaryRelation.includes("Grandson")) {
//     primaryHeadOfLine = "Gr Grandson of Head";
//   } else if (primaryRelation.includes("Granddaughter")) {
//     primaryHeadOfLine = "Gr Granddaughter of Head";
//   } else if (primaryRelation.includes("Great-Grandson")) {
//     primaryHeadOfLine = "Gr Gr Grandson of Head";
//   } else if (primaryRelation.includes("Great-Granddaughter")) {
//     primaryHeadOfLine = "Gr Gr Granddaughter of Head";
//   } else {
//     // Default fallback - remove generation prefixes and add "of Head"
//     primaryHeadOfLine = primaryRelation
//       .replace("Great-", "")
//       .replace("Grand", "")
//       .replace(" - Root Head of Family", "")
//       .replace(" - Spouse", "")
//       .trim() + " of Head";
//   }

//   // Spouse Head of Line relation
//   let spouseHeadOfLine = "";
//   if (isRootGeneration) {
//     spouseHeadOfLine = "Spouse of Head of Line";
//   } else if (spouseRelation.includes("Daughter-in-law") && primaryRelation.includes("Son")) {
//     spouseHeadOfLine = "Wife of Head";
//   } else if (spouseRelation.includes("Son-in-law") && primaryRelation.includes("Daughter")) {
//     spouseHeadOfLine = "Husband of Head";
//   } else if (spouseRelation.includes("Daughter-in-law") && primaryRelation.includes("Grandson")) {
//     spouseHeadOfLine = "Wife of Son of Head";
//   } else if (spouseRelation.includes("Son-in-law") && primaryRelation.includes("Granddaughter")) {
//     spouseHeadOfLine = "Husband of Daughter of Head";
//   } else if (spouseRelation.includes("Daughter-in-law") && primaryRelation.includes("Great-Grandson")) {
//     spouseHeadOfLine = "Wife of Grandson of Head";
//   } else if (spouseRelation.includes("Son-in-law") && primaryRelation.includes("Great-Granddaughter")) {
//     spouseHeadOfLine = "Husband of Granddaughter of Head";
//   } else {
//     // Default fallback for spouses
//     spouseHeadOfLine = spouseRelation
//       .replace("Great-", "")
//       .replace("Grand", "")
//       .replace(" - Root Head of Family", "")
//       .replace(" - Spouse", "")
//       .trim() + " of Head";
//   }

//   return {
//     primaryHeadOfLineRelation: primaryHeadOfLine,
//     spouseHeadOfLineRelation: spouseHeadOfLine
//   };
// }

// export function calculateHeadOfLineFromFamilyLabels(familyLabels, isRootGeneration = false) {
//   const { primaryRelation, spouseRelation } = familyLabels;

//   console.log("*************************************")
//   console.log("primary relation : ",primaryRelation)
  
//   // Primary member Head of Line relation
//   let primaryHeadOfLine = "";
//   if (isRootGeneration) {
//     primaryHeadOfLine = "Self (Head of Line)";
//   } else if (primaryRelation.includes("Son") && !primaryRelation.includes("Grand")) {
//     primaryHeadOfLine = "Self (Head of Line)";
//   } else if (primaryRelation.includes("Daughter") && !primaryRelation.includes("Grand")) {
//     primaryHeadOfLine = "Self (Head of Line)";
//   } else if (primaryRelation.includes("Grandson")) {
//     primaryHeadOfLine = "Son of Head";
//   } else if (primaryRelation.includes("Granddaughter")) {
//     primaryHeadOfLine = "Daughter of Head";
//   } else if (primaryRelation.includes("Gr Grandson")) {
//     primaryHeadOfLine = "Grandson of Head";
//   } else if (primaryRelation.includes("Gr Granddaughter")) {
//     primaryHeadOfLine = "Granddaughter of Head";
//   } else if (primaryRelation.includes("Gr Gr Grandson")) {
//     primaryHeadOfLine = "Gr Grandson of Head";
//   } else if (primaryRelation.includes("Gr Gr Granddaughter")) {
//     primaryHeadOfLine = "Gr Granddaughter of Head";
//   } else {
//     // Default fallback
//     primaryHeadOfLine = primaryRelation + " of Head";
//   }

// if (isRootGeneration) {
//     primaryHeadOfLine = "Self (Head of Line)";
//   } else if (primaryRelation.includes("Gr Gr Grandson")) {
//     primaryHeadOfLine = "Gr Grandson of Head";
//   } else if (primaryRelation.includes("Gr Gr Granddaughter")) {
//     primaryHeadOfLine = "Gr Granddaughter of Head";
//   } else if (primaryRelation.includes("Gr Grandson")) {
//     primaryHeadOfLine = "Grandson of Head";
//   } else if (primaryRelation.includes("Gr Granddaughter")) {
//     primaryHeadOfLine = "Granddaughter of Head";
//   } else if (primaryRelation.includes("Grandson")) {
//     primaryHeadOfLine = "Son of Head";
//   } else if (primaryRelation.includes("Granddaughter")) {
//     primaryHeadOfLine = "Daughter of Head";
//   } else if (primaryRelation.includes("Son") && !primaryRelation.includes("Grand")) {
//     primaryHeadOfLine = "Self (Head of Line)";
//   } else if (primaryRelation.includes("Daughter") && !primaryRelation.includes("Grand")) {
//     primaryHeadOfLine = "Self (Head of Line)";
//   } else {
//     // Default fallback
//     primaryHeadOfLine = primaryRelation + " of Head";
//   }

  // Spouse Head of Line relation - SIMPLIFIED
  // let spouseHeadOfLine = "";
  // if (isRootGeneration) {
  //   spouseHeadOfLine = "Spouse of Head of Line";
  // } else if (spouseRelation.includes("Daughter-in-law") && primaryRelation.includes("Son")) {
  //   spouseHeadOfLine = "Daughter-in-law of Head";
  // } else if (spouseRelation.includes("Son-in-law") && primaryRelation.includes("Daughter")) {
  //   spouseHeadOfLine = "Son-in-law of Head";
  // } else if (spouseRelation.includes("Daughter-in-law") && primaryRelation.includes("Grandson")) {
  //   spouseHeadOfLine = "Granddaughter-in-law of Head";
  // } else if (spouseRelation.includes("Son-in-law") && primaryRelation.includes("Granddaughter")) {
  //   spouseHeadOfLine = "Grandson-in-law of Head";
  // } else if (spouseRelation.includes("Daughter-in-law") && primaryRelation.includes("Great-Grandson")) {
  //   spouseHeadOfLine = "Great-Granddaughter-in-law of Head";
  // } else if (spouseRelation.includes("Son-in-law") && primaryRelation.includes("Great-Granddaughter")) {
  //   spouseHeadOfLine = "Great-Grandson-in-law of Head";
  // } else {
  //   // Default fallback for spouses
  //   spouseHeadOfLine = spouseRelation + " of Head";
  // }

  // return {
  //   primaryHeadOfLineRelation: primaryHeadOfLine,
  //   // spouseHeadOfLineRelation: spouseHeadOfLine
  // };
// }

export function calculateHeadOfLineFromFamilyLabels(familyLabels, isRootGeneration = false) {
  const { primaryRelation } = familyLabels;

  console.log("*************************************");


  function getHeadOfLineDynamic(relation, isRoot) {
    if (isRoot) return "Self";

    if (!relation || typeof relation !== "string") {
      return "Unknown of Head";
    }

    // normalize spacing, hyphens and case, but keep original words
    let normalized = relation
      .replace(/[-_]+/g, " ")           // unify hyphens/underscores to spaces
      .replace(/\s+/g, " ")             // collapse multiple spaces
      .trim();

    // count and strip any leading "gr" / "great" tokens (handles "Gr", "great-", "great ", etc.)
    let strippedCount = 0;
    while (true) {
      const m = normalized.match(/^(?:gr|great)(?:\s+|$)/i);
      if (!m) break;
      strippedCount += 1;
      normalized = normalized.slice(m[0].length).trim();
    }

    const low = normalized.toLowerCase();

    const isSonBase = /\bson\b/i.test(low); // matches "son" as a separate token
    const isDaughterBase = /\bdaughter\b/i.test(low); // matches "daughter"
    const isGrandsonBase = /\bgrandson\b/i.test(low);
    const isGranddaughterBase = /\bgranddaughter\b/i.test(low);

    // If base is plain Son/Daughter (no 'grand' in the base)
    if ((isSonBase || isDaughterBase) && !(isGrandsonBase || isGranddaughterBase)) {
      // plain Son/Daughter -> Self (Head of Line) but should be just "Self" for generation 1
      // However, this case is for generation 2 (Head of Line), so we check if relation is "Self"
      if (relation === "Self" || normalized.toLowerCase() === "self") {
        return "Self";
      }
      return "Self (Head of Line)";
    }

    // If base is 'grandson' or 'granddaughter', compute label according to strippedCount
    if (isGrandsonBase || isGranddaughterBase) {
      const baseIsSon = isGrandsonBase;
      // If no leading "gr" tokens were stripped, Grandson -> "Son of Head"
      if (strippedCount === 0) {
        return baseIsSon ? "Son" : "Daughter";
      }

      // If we stripped >= 1 gr tokens:
      // - strippedCount === 1 -> "Grandson of Head" / "Granddaughter of Head"
      // - strippedCount === 2 -> "Gr Grandson of Head" / ...
      // - strippedCount > 2 -> "Gr ".repeat(strippedCount - 1) + "Grandson of Head"
      const prefixCount = Math.max(0, strippedCount - 1);
      const prefix = prefixCount > 0 ? "Gr ".repeat(prefixCount) : "";
      const core = baseIsSon ? "Grandson" : "Granddaughter";
      const result = (prefix + core).trim();
      // Ensure proper capitalization (Grandson not GrandSon, Granddaughter not GrandDaughter)
      return normalizeRelation(result);
    }

    // Fallback: keep previous default behavior
    return relation + "";
  }

  const primaryHeadOfLine = getHeadOfLineDynamic(primaryRelation, isRootGeneration);
  console.log("primary relation : ", primaryRelation,' head of line relation : ',primaryHeadOfLine);
  return {
    primaryHeadOfLineRelation: primaryHeadOfLine
  };
}



